import * as Sentry from "@sentry/react"
import { AwesomeGraphQLClient } from "awesome-graphql-client"
import { parse as gqlParse } from "graphql"

import { API_ORIGIN, IS_DEV } from "@/lib/constants"
import { logResponse } from "@/src/components/Devtools"

function fetcher<
  TData extends Record<string, any>,
  TVariables extends Record<string, any>,
>(
  endpoint: string,
  operation: string,
  variables?: TVariables,
  headers?: RequestInit["headers"],
): () => Promise<TData> {
  const client = new AwesomeGraphQLClient({ endpoint })

  return async (): Promise<TData> => {
    const gql = gqlParse(operation)
    const operationDef = gql.definitions.find(
      (def) => def.kind === "OperationDefinition",
    )
    const opName = operationDef?.name?.value
    const opType = operationDef?.operation

    return Sentry.startSpan(
      {
        name: opName ? `${opType} ${opName}` : `graphql.${opType}`,
        op: `graphql.${opType}`,
        attributes: {
          "graphql.operation.name": opName,
          "graphql.operation.type": opType,
          "graphql.operation": operation,
          "graphql.variables": JSON.stringify(variables),
        },
      },
      async () => {
        if (IS_DEV) {
          // This recreates client.request so that dev/prod behavior is identical
          // while letting us observe the network response.
          // https://github.com/lynxtaa/awesome-graphql-client/blob/06eabb71/packages/awesome-graphql-client/src/AwesomeGraphQLClient.ts#L276-L308
          const res = await client.requestSafe<TData, TVariables>(
            operation,
            variables,
            {
              method: "POST",
              credentials: "include",
              headers,
            },
          )
          if (!res.ok) {
            throw res.error
          }
          logResponse(res.response)
          return res.data
        } else {
          return client.request<TData, TVariables>(operation, variables, {
            method: "POST",
            credentials: "include",
            headers,
          })
        }
      },
    )
  }
}

export function publicFetcher<
  TData extends Record<string, any>,
  TVariables extends Record<string, any>,
>(
  operation: string,
  variables?: TVariables,
  headers?: RequestInit["headers"],
): () => Promise<TData> {
  return fetcher(API_ORIGIN + "/graphql", operation, variables, headers)
}

export function adminFetcher<
  TData extends Record<string, any>,
  TVariables extends Record<string, any>,
>(
  operation: string,
  variables?: TVariables,
  headers?: RequestInit["headers"],
): () => Promise<TData> {
  return fetcher(API_ORIGIN + "/admin/graphql", operation, variables, headers)
}
